import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createResell, clearResell } from "../store/slices/resellSlice";
import { fetchAllUsers, fetchCustomerPlots } from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import {
  Users,
  MapPin,
  Calendar,
  CreditCard,
  FileText,
  Building2,
  UserPlus,
  PlusCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ResellManagement = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const users = useSelector((state) => state.auth.users) || [];
  // const { societies } = useSelector((state) => state.societies) || {
  //   societies: [],
  // };
  const { activeSociety, theme, gradient } = useSidebarContext();

  const societyUsers = activeSociety?.users || [];
  const filteredUsers = users.filter((user) =>
    societyUsers.some((societyUser) => societyUser._id === user._id)
  );
  const [formData, setFormData] = useState({
    plot_id: "",
    previous_customer_id: "",
    new_customer_id: "",
    seller_fee_type: "",
    seller_fee_value: "",
    purchaser_fee_type: "",
    purchaser_fee_value: "",
    // Legacy fields for backward compatibility
    seller_fee_percentage: "",
    purchaser_fee_percentage: "",
    resell_date: "",
    description: "",
  });
  const [previousCustomerPlots, setPreviousCustomerPlots] = useState([]);
  const [previousSocietyId, setPreviousSocietyId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [selectedPlot, setSelectedPlot] = useState(null);

  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchSocieties());
  }, [dispatch]);

  useEffect(() => {
    if (activeSociety?._id !== previousSocietyId) {
      setFormData({
        plot_id: "",
        previous_customer_id: "",
        new_customer_id: "",
        seller_fee_type: "",
        seller_fee_value: "",
        purchaser_fee_type: "",
        purchaser_fee_value: "",
        seller_fee_percentage: "",
        purchaser_fee_percentage: "",
        resell_date: "",
        description: "",
      });
      setFormErrors({});
      setPreviousCustomerPlots([]);
      setSelectedPlot(null);
      setPreviousSocietyId(activeSociety?._id);
    }
  }, [activeSociety?._id]);

  useEffect(() => {
    if (
      formData.previous_customer_id &&
      activeSociety?._id === previousSocietyId
    ) {
      dispatch(fetchCustomerPlots(formData.previous_customer_id)).then(
        (result) => {
          const societyPlots = (result.payload || []).filter((plot) =>
            activeSociety?.plots?.some(
              (societyPlot) => societyPlot._id === plot._id
            )
          );
          setPreviousCustomerPlots(societyPlots);
        }
      );
    } else {
      setPreviousCustomerPlots([]);
    }
  }, [
    formData.previous_customer_id,
    activeSociety,
    previousSocietyId,
    dispatch,
  ]);

  const handleDateChange = (date) => {
    if (date) {
      setFormData((prevData) => ({
        ...prevData,
        resell_date: date,
      }));
      if (formErrors.resell_date) {
        setFormErrors((prev) => ({
          ...prev,
          resell_date: "",
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.previous_customer_id) {
      errors.previous_customer_id = "Previous customer is required";
    }

    if (!formData.new_customer_id) {
      errors.new_customer_id = "New customer is required";
    }

    if (!formData.plot_id) {
      errors.plot_id = "Please select a plot";
    }

    // Validate seller fee (optional, but if type is provided, value must be provided and valid)
    if (formData.seller_fee_type && (!formData.seller_fee_value || formData.seller_fee_value === "")) {
      errors.seller_fee_value = "Seller fee value is required when fee type is selected";
    }
    if (!formData.seller_fee_type && formData.seller_fee_value && formData.seller_fee_value !== "") {
      errors.seller_fee_type = "Seller fee type is required when fee value is provided";
    }
    if (formData.seller_fee_type && formData.seller_fee_value && formData.seller_fee_value !== "") {
      const sellerFeeValue = parseFloat(formData.seller_fee_value);
      if (isNaN(sellerFeeValue) || sellerFeeValue < 0) {
        errors.seller_fee_value = "Seller fee value must be a non-negative number";
      }
      if (formData.seller_fee_type === "percentage" && sellerFeeValue > 100) {
        errors.seller_fee_value = "Seller fee percentage must be between 0 and 100";
      }
    }

    // Validate purchaser fee (optional, but if type is provided, value must be provided and valid)
    if (formData.purchaser_fee_type && (!formData.purchaser_fee_value || formData.purchaser_fee_value === "")) {
      errors.purchaser_fee_value = "Purchaser fee value is required when fee type is selected";
    }
    if (!formData.purchaser_fee_type && formData.purchaser_fee_value && formData.purchaser_fee_value !== "") {
      errors.purchaser_fee_type = "Purchaser fee type is required when fee value is provided";
    }
    if (formData.purchaser_fee_type && formData.purchaser_fee_value && formData.purchaser_fee_value !== "") {
      const purchaserFeeValue = parseFloat(formData.purchaser_fee_value);
      if (isNaN(purchaserFeeValue) || purchaserFeeValue < 0) {
        errors.purchaser_fee_value = "Purchaser fee value must be a non-negative number";
      }
      if (formData.purchaser_fee_type === "percentage" && purchaserFeeValue > 100) {
        errors.purchaser_fee_value = "Purchaser fee percentage must be between 0 and 100";
      }
    }

    if (!formData.resell_date) {
      errors.resell_date = "Resell date is required";
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Calculate fee amounts based on plot price and fee type/value
  const calculateFeeAmounts = () => {
    if (!selectedPlot || !selectedPlot.price) {
      return { sellerFeeAmount: 0, purchaserFeeAmount: 0 };
    }

    const plotPrice = parseFloat(selectedPlot.price) || 0;
    
    // Calculate seller fee
    let sellerFeeAmount = 0;
    if (formData.seller_fee_type && formData.seller_fee_value && formData.seller_fee_value !== "") {
      const sellerFeeValue = parseFloat(formData.seller_fee_value) || 0;
      if (formData.seller_fee_type === "percentage") {
        sellerFeeAmount = (plotPrice * sellerFeeValue) / 100;
      } else if (formData.seller_fee_type === "fixed") {
        sellerFeeAmount = sellerFeeValue;
      }
    }

    // Calculate purchaser fee
    let purchaserFeeAmount = 0;
    if (formData.purchaser_fee_type && formData.purchaser_fee_value && formData.purchaser_fee_value !== "") {
      const purchaserFeeValue = parseFloat(formData.purchaser_fee_value) || 0;
      if (formData.purchaser_fee_type === "percentage") {
        purchaserFeeAmount = (plotPrice * purchaserFeeValue) / 100;
      } else if (formData.purchaser_fee_type === "fixed") {
        purchaserFeeAmount = purchaserFeeValue;
      }
    }

    return { sellerFeeAmount, purchaserFeeAmount };
  };

  const { sellerFeeAmount, purchaserFeeAmount } = calculateFeeAmounts();

  const handleCreate = async () => {
    if (!validateForm()) {
      enqueueSnackbar("Please fill in all required fields", {
        variant: "error",
      });
      return;
    }
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to create this resell record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, create it!",
      background: "linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)",
      color: "#5EEAD4",
    });

    if (result.isConfirmed) {
      try {
        // Prepare data for submission - convert empty strings to null for optional fields
        const submitData = {
          plot_id: formData.plot_id,
          previous_customer_id: formData.previous_customer_id,
          new_customer_id: formData.new_customer_id,
          seller_fee_type: formData.seller_fee_type || null,
          seller_fee_value: formData.seller_fee_value && formData.seller_fee_value !== "" ? parseFloat(formData.seller_fee_value) : null,
          purchaser_fee_type: formData.purchaser_fee_type || null,
          purchaser_fee_value: formData.purchaser_fee_value && formData.purchaser_fee_value !== "" ? parseFloat(formData.purchaser_fee_value) : null,
          resell_date: formData.resell_date,
          description: formData.description,
        };
        
        const response = await dispatch(createResell(submitData));
        if (response?.type === "resell/createResell/fulfilled") {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response?.payload?.message ||
              "Resell record created successfully.",
            confirmButtonColor: "#008000",
            background:
              "linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)",
            color: "#5EEAD4",
          });
          setFormData({
            plot_id: "",
            previous_customer_id: "",
            new_customer_id: "",
            seller_fee_type: "",
            seller_fee_value: "",
            purchaser_fee_type: "",
            purchaser_fee_value: "",
            seller_fee_percentage: "",
            purchaser_fee_percentage: "",
            resell_date: "",
            description: "",
          });
          setFormErrors({});
          setSelectedPlot(null);
          enqueueSnackbar(
            response?.payload?.message || "Created successfully resell record.",
            {
              variant: "success",
            }
          );
          dispatch(clearResell());
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: response?.payload?.message || "An unexpected error occurred.",
            confirmButtonColor: "#f27474",
            background:
              "linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)",
            color: "#FCA5A5",
          });
          enqueueSnackbar(
            response?.payload?.message || "Failed to create resell record.",
            {
              variant: "error",
            }
          );
        }
      } catch (error) {
        console.error("Error creating resell record:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while creating the record.",
          confirmButtonColor: "#f27474",
          background:
            "linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)",
          color: "#FCA5A5",
        });
        enqueueSnackbar("An error occurred. Please try again.", {
          variant: "error",
        });
      }
    }
  };

  const stats = [
    {
      title: "Total Plots",
      value: previousCustomerPlots.length,
      icon: <Building2 className="w-6 h-6" />,
      description: "Available for resell",
    },
    {
      title: "Total Customers",
      value:
        activeSociety?.users?.filter((user) => user.role === "Customer")
          .length || 0,
      icon: <Users className="w-6 h-6" />,
      description: "Active in system",
    },
    {
      title: `Total Blocks in this Society - ${
        [...new Set(activeSociety?.plots?.map((plot) => plot.block))].length ||
        0
      }`,

      icon: <MapPin className="w-6 h-6" />,
      description: "Registered blocks",
      blockNamesArray: [
        ...new Set(activeSociety?.plots?.map((plot) => plot.block)),
      ],
    },
  ];

  return (
    <div className={`min-h-screen ${gradient}`}>
      <div className="max-w-7xl mx-auto p-6 lg:p-8 space-y-8">
        {/* Header Section */}
        <div
          className={`relative overflow-hidden rounded-2xl ${theme.tailwind.bg9} backdrop-blur-xl border ${theme.tailwind.border2} p-8`}
        >
          <div className={`absolute inset-0 ${theme.tailwind.bgGradient8}`} />
          <div
            className={`absolute -top-24 -right-24 w-48 h-48 ${theme.tailwind.bg1} rounded-full blur-3xl`}
          />
          <div
            className={`absolute -bottom-24 -left-24 w-48 h-48 ${theme.tailwind.bg33} rounded-full blur-3xl`}
          />
          <div className="relative">
            <h3
              className={`text-2xl font-bold mt-2 mb-2 ${theme.tailwind.text}`}
            >
              Manage property transfers and resell operations
            </h3>
            <h1
              className={`text-4xl font-bold ${theme.tailwind.bgr5} bg-clip-text text-transparent`}
            >
              {activeSociety?.name || "Society Name"}
            </h1>

            <div className="flex items-center gap-2 mb-4">
              <MapPin className={`w-6 h-6 ${theme.tailwind.text}`} />
              <h2 className={`text-lg font-medium ${theme.tailwind.text9}`}>
                {activeSociety?.location || "Location"}
              </h2>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-2xl ${theme.tailwind.bg2} backdrop-blur-xl border ${theme.tailwind.border2} p-6 transition-all duration-300 ${theme.tailwind.bgHover20} ${theme.tailwind.hoverB1}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-xl ${theme.tailwind.bgGradient9} ${theme.tailwind.text9}`}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className={`text-sm ${theme.tailwind.text13}`}>
                    {stat.title}
                  </p>
                  <p className={`text-3xl font-bold ${theme.tailwind.text9}`}>
                    {stat.value}
                  </p>
                  {stat.blockNamesArray && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {stat.blockNamesArray.map((block, index) => (
                        <span
                          key={index}
                          className={`${theme.tailwind.bg35} ${theme.tailwind.text9} font-semibold px-3 py-1 rounded-full text-sm shadow-md`}
                        >
                          {block}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className={`text-xs ${theme.tailwind.text14} mt-1`}>
                    {stat.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Form Section */}
        <div
          className={`rounded-2xl ${theme.tailwind.bg2} backdrop-blur-xl border ${theme.tailwind.border2} p-8`}
        >
          <div className="space-y-8">
            {/* Customer Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Previous Customer Select */}
              <div className="space-y-2">
                <label
                  className={`block text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Previous Customer
                </label>
                <div className="relative">
                  <Users
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                  />
                  <select
                    name="previous_customer_id"
                    value={formData.previous_customer_id}
                    onChange={handleInputChange}
                    className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                  >
                    <option value="">Select Customer</option>
                    {filteredUsers
                      .filter((user) => user.role === "Customer")
                      .map((user) => (
                        <option
                          key={user._id}
                          value={user._id}
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          {user.username}
                        </option>
                      ))}
                  </select>
                </div>
                {formErrors.previous_customer_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.previous_customer_id}
                  </p>
                )}
              </div>

              {/* New Customer Select */}
              <div className="space-y-2">
                <label
                  className={`block text-sm font-medium ${theme.tailwind.text9}`}
                >
                  New Customer
                </label>
                <div className="relative">
                  <UserPlus
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                  />
                  <select
                    name="new_customer_id"
                    value={formData.new_customer_id}
                    onChange={handleInputChange}
                    className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                  >
                    <option value="">Select New Customer</option>
                    {filteredUsers
                      .filter(
                        (user) =>
                          user.role === "Customer" &&
                          user._id !== formData.previous_customer_id
                      )
                      .map((user) => (
                        <option
                          key={user._id}
                          value={user._id}
                          className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                        >
                          {user.username}
                        </option>
                      ))}
                  </select>
                </div>
                {formErrors.new_customer_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.new_customer_id}
                  </p>
                )}
              </div>
            </div>

            {/* Plots Grid */}
            {previousCustomerPlots.length > 0 && (
              <div className="space-y-4">
                <h3 className={`text-xl font-semibold ${theme.tailwind.text9}`}>
                  Available Plots
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {previousCustomerPlots.map((plot) => (
                    <div
                      key={plot._id}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, plot_id: plot._id }));
                        setSelectedPlot(plot);
                      }}
                      className={`relative overflow-hidden rounded-xl cursor-pointer transition-all duration-300 ${
                        formData.plot_id === plot._id
                          ? `${theme.tailwind.bgGradient10} ${theme.tailwind.border6}`
                          : `${theme.tailwind.bg34} ${theme.tailwind.bgHover20}`
                      } border ${theme.tailwind.border2} p-4 group`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span
                            className={`${theme.tailwind.text9} font-medium`}
                          >
                            Plot #{plot.plot_number}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${theme.tailwind.bg3} ${theme.tailwind.texting1}`}
                          >
                            {plot.category}
                          </span>
                        </div>
                        <div
                          className={`space-y-1 text-sm ${theme.tailwind.text13}`}
                        >
                          <p>Block: {plot.block}</p>
                          <p>Type: {plot.plot_type}</p>
                          <p>Status: {plot.status}</p>
                          <p>Total Plot Amount: {plot.price}</p>
                          <p>
                            Plot size: {plot.size}sq(ft) Plot Size in Marla:{" "}
                            {plot.marla}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`absolute inset-0 border border-teal-500/0 ${theme.tailwind.groupHover8} rounded-xl transition-all duration-300`}
                      />
                    </div>
                  ))}
                </div>
                {formErrors.plot_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.plot_id}
                  </p>
                )}
              </div>
            )}

            {/* Additional Fields */}
            {formData.plot_id && selectedPlot && (
              <div className="space-y-6">
                {/* Plot Price Display */}
                <div className={`rounded-xl ${theme.tailwind.bg34} border ${theme.tailwind.border19} p-4`}>
                  <p className={`text-sm ${theme.tailwind.text13} mb-1`}>Total Plot Amount</p>
                  <p className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                    {selectedPlot.price?.toLocaleString() || "0"} PKR
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Seller Fee Input */}
                  <div className="space-y-2">
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Seller Fee (Optional)
                    </label>
                    <div className="space-y-2">
                      <div className="relative">
                        <CreditCard
                          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                        />
                        <select
                          name="seller_fee_type"
                          value={formData.seller_fee_type}
                          onChange={handleInputChange}
                          className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                        >
                          <option value="">Select Fee Type</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (PKR)</option>
                        </select>
                      </div>
                      {formErrors.seller_fee_type && (
                        <p className="text-red-500 text-sm mt-1">
                          {formErrors.seller_fee_type}
                        </p>
                      )}
                      {formData.seller_fee_type && (
                        <div className="relative">
                          <CreditCard
                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                          />
                          <input
                            type="number"
                            name="seller_fee_value"
                            value={formData.seller_fee_value}
                            onChange={handleInputChange}
                            min="0"
                            max={formData.seller_fee_type === "percentage" ? "100" : undefined}
                            step="0.01"
                            className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                            placeholder={
                              formData.seller_fee_type === "percentage"
                                ? "Enter percentage (e.g., 2.5)"
                                : "Enter fixed amount (e.g., 50000)"
                            }
                          />
                        </div>
                      )}
                      {formErrors.seller_fee_value && (
                        <p className="text-red-500 text-sm mt-1">
                          {formErrors.seller_fee_value}
                        </p>
                      )}
                      {formData.seller_fee_type && formData.seller_fee_value && !formErrors.seller_fee_value && (
                        <p className={`text-sm ${theme.tailwind.text13} mt-1`}>
                          Calculated Amount: <span className={`font-semibold ${theme.tailwind.text9}`}>
                            {sellerFeeAmount.toLocaleString()} PKR
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Purchaser Fee Input */}
                  <div className="space-y-2">
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Purchaser Fee (Optional)
                    </label>
                    <div className="space-y-2">
                      <div className="relative">
                        <CreditCard
                          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                        />
                        <select
                          name="purchaser_fee_type"
                          value={formData.purchaser_fee_type}
                          onChange={handleInputChange}
                          className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                        >
                          <option value="">Select Fee Type</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (PKR)</option>
                        </select>
                      </div>
                      {formErrors.purchaser_fee_type && (
                        <p className="text-red-500 text-sm mt-1">
                          {formErrors.purchaser_fee_type}
                        </p>
                      )}
                      {formData.purchaser_fee_type && (
                        <div className="relative">
                          <CreditCard
                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                          />
                          <input
                            type="number"
                            name="purchaser_fee_value"
                            value={formData.purchaser_fee_value}
                            onChange={handleInputChange}
                            min="0"
                            max={formData.purchaser_fee_type === "percentage" ? "100" : undefined}
                            step="0.01"
                            className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                            placeholder={
                              formData.purchaser_fee_type === "percentage"
                                ? "Enter percentage (e.g., 2.5)"
                                : "Enter fixed amount (e.g., 50000)"
                            }
                          />
                        </div>
                      )}
                      {formErrors.purchaser_fee_value && (
                        <p className="text-red-500 text-sm mt-1">
                          {formErrors.purchaser_fee_value}
                        </p>
                      )}
                      {formData.purchaser_fee_type && formData.purchaser_fee_value && !formErrors.purchaser_fee_value && (
                        <p className={`text-sm ${theme.tailwind.text13} mt-1`}>
                          Calculated Amount: <span className={`font-semibold ${theme.tailwind.text9}`}>
                            {purchaserFeeAmount.toLocaleString()} PKR
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Total Fee Summary */}
                {(sellerFeeAmount > 0 || purchaserFeeAmount > 0) && (
                  <div className={`rounded-xl ${theme.tailwind.bg34} border ${theme.tailwind.border19} p-4`}>
                    <p className={`text-sm font-medium ${theme.tailwind.text9} mb-2`}>Fee Summary</p>
                    <div className="space-y-1">
                      {sellerFeeAmount > 0 && (
                        <div className="flex justify-between">
                          <span className={`text-sm ${theme.tailwind.text13}`}>Seller Fee:</span>
                          <span className={`text-sm font-semibold ${theme.tailwind.text9}`}>
                            {sellerFeeAmount.toLocaleString()} PKR
                          </span>
                        </div>
                      )}
                      {purchaserFeeAmount > 0 && (
                        <div className="flex justify-between">
                          <span className={`text-sm ${theme.tailwind.text13}`}>Purchaser Fee:</span>
                          <span className={`text-sm font-semibold ${theme.tailwind.text9}`}>
                            {purchaserFeeAmount.toLocaleString()} PKR
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-gray-600">
                        <span className={`text-sm font-medium ${theme.tailwind.text9}`}>Total Fees:</span>
                        <span className={`text-sm font-bold ${theme.tailwind.text9}`}>
                          {(sellerFeeAmount + purchaserFeeAmount).toLocaleString()} PKR
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resell Date Input */}
                <div className="space-y-2">
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.text9}`}
                  >
                    Resell Date
                  </label>
                  <div className="relative">
                    <Calendar
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.tailwind.text13} w-5 h-5`}
                    />
                    <DatePicker
                      selected={
                        formData.resell_date
                          ? new Date(formData.resell_date)
                          : null
                      }
                      onChange={handleDateChange}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/YYYY"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                      wrapperClassName="w-full"
                      className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                      maxDate={new Date()}
                    />
                  </div>
                  {formErrors.resell_date && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.resell_date}
                    </p>
                  )}
                </div>

                {/* Description Textarea */}
                <div className="space-y-2">
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.text9}`}
                  >
                    Description
                  </label>
                  <div className="relative">
                    <FileText
                      className={`absolute left-3 top-3 ${theme.tailwind.text13} w-5 h-5`}
                    />
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      className={`w-full ${theme.tailwind.bg34} border ${theme.tailwind.border19} rounded-xl pl-12 pr-4 py-3 ${theme.tailwind.texting1} focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-200`}
                      placeholder="Enter description"
                    />
                  </div>
                  {formErrors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.description}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleCreate}
                  className={`w-full group relative overflow-hidden rounded-xl ${theme.tailwind.bgGradient11} px-8 py-3 text-white transition-all duration-300 ${theme.tailwind.groupHoverG3}`}
                >
                  <div className="relative flex items-center justify-center gap-2">
                    <PlusCircle className="w-5 h-5" />
                    <span className="font-medium">Resell the Plot</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResellManagement;
